// Two-level catalog taxonomy: Section -> Playlist (topic) -> ordered videos.
//
// Per the Catalog API ask (CATALOG-API-PLAYLISTS-ASK.md), playlists are
// DERIVED FROM THE VIDEO ROWS: each video carries section_id / playlist_id /
// playlist_title / order_in_playlist / is_intro and the UI builds the tree.
// The backend already stores and returns arbitrary fields, so no schema
// migration is needed -- a tagged row just works.
//
// Sections are the stable, small top-level set. We keep their display titles
// and order here (rarely change, and there's nowhere cleaner to hang
// section-level metadata under the derive-from-rows model). Playlists are
// fully data-driven.

export const SECTIONS = [
  {id: "career_pathways", title: "Career Pathways", icon: "🧭", order: 1},
  {id: "job_skills", title: "Job Skills", icon: "💼", order: 2},
  {id: "life_skills", title: "Life Skills", icon: "🌱", order: 3},
  {id: "ged", title: "GED Prep", icon: "🎓", order: 4},
];

// DEV SEED — temporary. Today's catalog isn't tagged with section_id/
// playlist_id yet; it only has the legacy `category` string. Until videos are
// tagged for real (admin Upload/Edit flow, Phase 3), we map the existing
// category onto a section + playlist so the playlist UI has real data to
// render. When a row carries explicit playlist fields they win over this seed,
// so this block becomes dead weight (and can be deleted) once tagging lands.
//   category (lowercased) -> [section_id, playlist_id, playlist_title]
const CATEGORY_SEED = {
  "career exploration": [
    "career_pathways",
    "explore-careers",
    "Explore Careers",
  ],
  "job training": ["job_skills", "workplace-readiness", "Workplace Readiness"],
  "essential skills": ["job_skills", "essential-skills", "Essential Skills"],
  "financial literacy": [
    "life_skills",
    "financial-literacy",
    "Financial Literacy",
  ],
  "healthy lifestyle": [
    "life_skills",
    "healthy-lifestyle",
    "Healthy Lifestyle",
  ],
  "health literacy": ["life_skills", "health-literacy", "Health & the Body"],
  math: ["ged", "ged-math", "Math"],
  "social studies": ["ged", "ged-social-studies", "Social Studies"],
  science: ["ged", "ged-science", "Science"],
  "language arts": ["ged", "ged-rla", "Reading & Language Arts"],
  ged: ["ged", "ged-core", "GED Core & Practice"],
};

// Resolve a video's playlist tagging. Explicit row fields always win; the
// category seed is only a fallback for untagged rows.
export const tagVideo = (video) => {
  const v = video || {};
  const cat = (v.category || "").toLowerCase().trim();

  // Explicit intro flag, or legacy "Orientation" category, pins the video.
  if (v.is_intro === true || cat === "orientation") {
    return {is_intro: true};
  }

  // Explicit tagging from the row.
  if (v.section_id && v.playlist_id) {
    return {
      section_id: v.section_id,
      playlist_id: v.playlist_id,
      playlist_title: v.playlist_title || v.playlist_id,
      order_in_playlist: v.order_in_playlist,
    };
  }

  // Dev seed fallback by legacy category.
  const seed = CATEGORY_SEED[cat];
  if (seed) {
    return {
      section_id: seed[0],
      playlist_id: seed[1],
      playlist_title: seed[2],
      order_in_playlist: v.order_in_playlist,
    };
  }

  return {}; // untagged -> handled by buildCatalog's fallback bucket
};
